// src/App.tsx - Main application component

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { theme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { useSelector } from 'react-redux';
import { RootState } from './store';

// Import pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import AssessmentPage from './pages/onboarding/AssessmentPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import JournalPage from './pages/journal/JournalPage';
import JournalEntryPage from './pages/journal/JournalEntryPage';
import ReflectionPage from './pages/journal/ReflectionPage';
import RelapseTrackerPage from './pages/relapse/RelapseTrackerPage';
import AddRelapsePage from './pages/relapse/AddRelapsePage';
import TriggersPage from './pages/relapse/TriggersPage';
import SettingsPage from './pages/settings/SettingsPage';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

// Assessment check route
const AssessmentCheckRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const assessmentComplete = useSelector((state: RootState) => state.profile.assessmentComplete);
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!assessmentComplete) {
    return <Navigate to="/onboarding" />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <Router>
              <Routes>
                {/* Auth routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                
                {/* Onboarding routes */}
                <Route 
                  path="/onboarding" 
                  element={
                    <ProtectedRoute>
                      <OnboardingPage />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/assessment" 
                  element={
                    <ProtectedRoute>
                      <AssessmentPage />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Main app routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <AssessmentCheckRoute>
                      <DashboardPage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/journal" 
                  element={
                    <AssessmentCheckRoute>
                      <JournalPage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/journal/entry/:entryId?" 
                  element={
                    <AssessmentCheckRoute>
                      <JournalEntryPage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/journal/reflection/:entryId" 
                  element={
                    <AssessmentCheckRoute>
                      <ReflectionPage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/relapse" 
                  element={
                    <AssessmentCheckRoute>
                      <RelapseTrackerPage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/relapse/add/:incidentId?" 
                  element={
                    <AssessmentCheckRoute>
                      <AddRelapsePage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/relapse/triggers" 
                  element={
                    <AssessmentCheckRoute>
                      <TriggersPage />
                    </AssessmentCheckRoute>
                  } 
                />
                <Route 
                  path="/settings" 
                  element={
                    <AssessmentCheckRoute>
                      <SettingsPage />
                    </AssessmentCheckRoute>
                  } 
                />
                
                {/* Default route */}
                <Route path="/" element={<Navigate to="/dashboard" />} />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </PersistGate>
    </ReduxProvider>
  );
}

export default App;
